<template>
  <div class="flex w-full">
    <!-- Left Column - Leagues Search and Results -->
    <div class="w-80 border-r border-gray-200 bg-gray-50 p-4 flex flex-col">
      <!-- Leagues Section -->
      <div class="bg-white border border-gray-200 rounded-lg p-4 flex-1 flex flex-col min-h-0">
        <div class="flex items-center space-x-2 mb-3 flex-shrink-0">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">Search Leagues</label>
            <div class="relative">
              <input
                v-model="leagueSearch"
                @input="searchLeagues"
                type="text"
                placeholder="Search for leagues..."
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <div v-if="isSearchingLeagues" class="absolute right-3 top-2">
                <i class="fas fa-spinner fa-spin text-gray-400 text-sm"></i>
              </div>
            </div>
          </div>
          <button
            @click="clearLeagueSearch"
            class="px-2 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            title="Clear search"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Leagues Results -->
        <div v-if="leagueResults.length > 0" class="flex-1 overflow-y-auto min-h-0">
          <h4 class="text-sm font-medium text-gray-700 mb-3 flex-shrink-0">
            {{ leagueSearch ? `Results (${leagueResults.length})` : `All Leagues (${leagueResults.length})` }}
          </h4>
          <div class="space-y-2 pr-2">
            <div
              v-for="(league, index) in leagueResults"
              :key="league.id"
              class="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
              :class="selectedLeague?.id === league.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''"
              @click="openLeagueDetails(league)"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <h5 class="font-medium text-gray-800 text-sm truncate">{{ league.league_name }}</h5>
                  <div class="text-xs text-gray-600 mt-1">
                    <p v-if="league.region" class="truncate">{{ league.region }}</p>
                    <p v-if="league.country" class="truncate">{{ league.country }}</p>
                  </div>
                </div>
                <div class="text-gray-400 ml-2 flex-shrink-0">
                  <i class="fas fa-chevron-right text-xs"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="leagueSearch && !isSearchingLeagues" class="flex-1 flex items-center justify-center min-h-0">
          <div class="text-center text-gray-500 text-sm">
            No leagues found
          </div>
        </div>

        <div v-else class="flex-1 flex items-center justify-center min-h-0">
          <div class="text-center text-gray-500 text-sm">
            <i class="fas fa-trophy text-2xl mb-2"></i>
            <p>Search for leagues to get started</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - League Details Display -->
    <div class="flex-1 p-6 flex flex-col min-h-0">
      <!-- League Details Section -->
      <div v-if="selectedLeague" class="flex-1 flex flex-col space-y-4 min-h-0">
        <!-- League Navigation Header -->
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <h3 class="text-lg font-semibold text-gray-800">
                {{ selectedLeague.league_name }}
              </h3>
              <div class="text-sm text-gray-500 space-y-1">
                <div v-if="selectedLeague.region || selectedLeague.country">
                  <span v-if="selectedLeague.region">{{ selectedLeague.region }}</span>
                  <span v-if="selectedLeague.country"> • {{ selectedLeague.country }}</span>
                </div>
                <div>
                  {{ currentLeagueIndex + 1 }} of {{ leagueResults.length }} results
                </div>
              </div>
              <div v-if="isLoadingLeague" class="flex items-center text-blue-600">
                <i class="fas fa-spinner fa-spin mr-2"></i>
                <span class="text-sm">Loading...</span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="previousLeague"
                :disabled="currentLeagueIndex === 0 || isLoadingLeague"
                class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous league"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <button
                @click="nextLeague"
                :disabled="currentLeagueIndex === leagueResults.length - 1 || isLoadingLeague"
                class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next league"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoadingLeague" class="flex items-center justify-center py-12">
          <div class="text-center text-gray-500">
            <i class="fas fa-spinner fa-spin text-4xl mb-4"></i>
            <p class="text-lg">Loading league details...</p>
          </div>
        </div>

        <!-- League Details Content -->
        <div v-else class="bg-white border border-gray-200 rounded-lg p-6 flex-1 flex flex-col min-h-0">
          <div class="flex-1 flex flex-col min-h-0">
            <div class="flex-shrink-0">
              <h4 class="text-lg font-medium text-gray-800 mb-2">League Information</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">League Name</label>
                  <input
                    v-model="selectedLeague.league_name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Region</label>
                  <input
                    v-model="selectedLeague.region"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  v-model="selectedLeague.country"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <!-- Teams in this League -->
            <div class="flex-1 flex flex-col min-h-0 mt-4">
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-lg font-medium text-gray-800">Teams in League ({{ selectedLeague.teams?.length || 0 }})</h4>
                <button
                  @click="showAddTeamSearch = !showAddTeamSearch"
                  class="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <i class="fas fa-plus mr-1"></i>
                  Add Team
                </button>
              </div>
              
              <!-- Add Team Search -->
              <div v-if="showAddTeamSearch" class="mb-4 bg-white border border-gray-200 rounded-lg p-4">
                <div class="flex items-center space-x-2 mb-3">
                  <div class="flex-1">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Search Teams to Add</label>
                    <div class="relative">
                      <input
                        v-model="addTeamSearch"
                        @input="searchTeamsToAdd"
                        type="text"
                        placeholder="Search for teams to add to this league..."
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <div v-if="isSearchingAddTeam" class="absolute right-3 top-2">
                        <i class="fas fa-spinner fa-spin text-gray-400 text-sm"></i>
                      </div>
                    </div>
                  </div>
                  <button
                    @click="clearAddTeamSearch"
                    class="px-2 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    title="Clear search"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                
                <!-- Add Team Search Results -->
                <div v-if="addTeamResults.length > 0" class="max-h-32 overflow-y-auto space-y-2">
                  <div
                    v-for="team in addTeamResults"
                    :key="team.id"
                    class="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                  >
                    <div class="flex-1">
                      <span class="text-sm font-medium text-gray-800">{{ team.team_name }}</span>
                      <div class="text-xs text-gray-500">
                        <span v-if="team.region">Region: {{ team.region }} • League: {{ team.league }}</span>
                      </div>
                    </div>
                    <button
                      @click="addTeamToLeague(team)"
                      class="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                      title="Add to league"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
                
                <div v-else-if="addTeamSearch && !isSearchingAddTeam" class="text-center text-gray-500 text-sm py-2">
                  No teams found
                </div>
              </div>
              
              <LeagueTeamsDisplay
                :teams="selectedLeague.teams"
                :show-add-team-search="showAddTeamSearch"
                :add-team-search="addTeamSearch"
                :add-team-results="addTeamResults"
                :is-searching-add-team="isSearchingAddTeam"
                :projections-stats="projectionsStats"
                :filtered-projections-stats="filteredProjectionsStats"
                @toggle-add-team="showAddTeamSearch = !showAddTeamSearch"
                @search-teams="handleSearchTeamsInput"
                @clear-add-team-search="clearAddTeamSearch"
                @add-team="addTeamToLeague"
                @remove-team="removeTeamFromLeague"
                @update:addTeamSearch="addTeamSearch = $event"
                @add-projection-team="handleAddProjectionTeam"
              />
            </div>

            <!-- Projections Stats Section -->
            <div class="flex-1 flex flex-col min-h-0 mt-4">
              <ProjectionsStatsDisplay
                :stats="filteredProjectionsStats"
                :is-loading="isLoadingProjectionsStats"
                :is-visible="showProjectionsStats"
                :search-query="projectionsStatsSearchQuery"
                :current-league="selectedLeague"
                @toggle-visibility="showProjectionsStats = !showProjectionsStats"
                @search-stats="handleProjectionsStatsSearch"
                @update:searchQuery="projectionsStatsSearchQuery = $event"

              />
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-end space-x-3 flex-shrink-0 mt-4">
              <button
                @click="saveLeagueSettings"
                class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <i class="fas fa-save mr-2"></i>
                Save League
              </button>
              <button
                @click="deleteLeague"
                class="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <i class="fas fa-trash mr-2"></i>
                Delete League
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!selectedLeague" class="flex items-center justify-center h-full">
        <div class="text-center text-gray-500">
          <i class="fas fa-trophy text-4xl mb-4"></i>
          <p class="text-lg">Select a league to view its details</p>
          <p class="text-sm">Click on any league from the list to view its information</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../axios.js'
import ProjectionsStatsDisplay from './ProjectionsStatsDisplay.vue'
import LeagueTeamsDisplay from './LeagueTeamsDisplay.vue'

export default {
  name: 'LeagueManagement',
  components: {
    ProjectionsStatsDisplay,
    LeagueTeamsDisplay
  },
  data() {
    return {
      leagueSearch: '',
      leagueResults: [],
      isSearchingLeagues: false,
      selectedLeague: null,
      currentLeagueIndex: 0,
      isLoadingLeague: false,
      showAddTeamSearch: false,
      addTeamSearch: '',
      addTeamResults: [],
      isSearchingAddTeam: false,
      leagueSearchTimeout: null,
      addTeamSearchTimeout: null,
      // New properties for projections stats
      projectionsStats: [],
      isLoadingProjectionsStats: false,
      showProjectionsStats: false,
      projectionsStatsSearch: '',
      filteredProjectionsStats: [],
      projectionsStatsSearchQuery: ''
    }
  },
  
  mounted() {
    // Load all leagues by default when component is mounted
    this.loadAllLeagues()
    // Load projections stats
    this.loadProjectionsStats()
  },
  
  methods: {
    async loadAllLeagues() {
      this.isSearchingLeagues = true
      try {
        // Fetch all leagues from the API
        const response = await axios.get('/api/v2/all-leagues')
        console.log('All leagues loaded:', response.data)
        this.leagueResults = response.data
      } catch (error) {
        console.error('Error loading all leagues:', error)
        this.leagueResults = []
      } finally {
        this.isSearchingLeagues = false
      }
    },

    // New method to fetch projections league stats
    async loadProjectionsStats() {
      this.isLoadingProjectionsStats = true
      try {
        const response = await axios.get('/api/v2/projections-leagues/all-leagues-stats')
        console.log('Projections stats loaded:', response.data)
        this.projectionsStats = response.data
        this.filteredProjectionsStats = response.data
      } catch (error) {
        console.error('Error loading projections stats:', error)
        this.projectionsStats = []
        this.filteredProjectionsStats = []
      } finally {
        this.isLoadingProjectionsStats = false
      }
    },

    // Method to search and match projections stats with current league
    async searchProjectionsStats() {
      if (!this.selectedLeague) return
      
      const leagueName = this.selectedLeague.league_name?.toLowerCase()
      if (!leagueName) return
      
      // Filter projections stats that match the current league
      this.filteredProjectionsStats = this.projectionsStats.filter(stat => {
        const statLeague = stat.league?.toLowerCase()
        return statLeague && statLeague.includes(leagueName) || leagueName.includes(statLeague)
      })
      
      console.log('Filtered projections stats for league:', leagueName, this.filteredProjectionsStats)
    },

    // Method to search projections stats using API
    async searchProjectionsStatsAPI() {
      if (!this.projectionsStatsSearchQuery || this.projectionsStatsSearchQuery.length < 2) {
        // If search is empty, load all stats
        await this.loadProjectionsStats()
        return
      }

      this.isLoadingProjectionsStats = true
      try {
        const response = await axios.post(`/api/v2/projections-leagues/all-leagues-stats/search`, {
          current_league: this.selectedLeague,
          selected_league: this.projectionsStatsSearchQuery
        })
        console.log('Projections stats search results:', response.data)
        this.filteredProjectionsStats = response.data
      } catch (error) {
        console.error('Error searching projections stats:', error)
        this.filteredProjectionsStats = []
      } finally {
        this.isLoadingProjectionsStats = false
      }
    },

    // Handle projections stats search input
    handleProjectionsStatsSearch(searchResults) {
      // If searchResults is provided, use it directly (from ProjectionsStatsDisplay)
      if (searchResults) {
        this.filteredProjectionsStats = searchResults
      } else {
        // Otherwise, perform API search
        this.searchProjectionsStatsAPI()
      }
    },
    
    async searchLeagues() {
      // Clear previous timeout
      if (this.leagueSearchTimeout) {
        clearTimeout(this.leagueSearchTimeout)
      }

      // Set new timeout for debounced search
      this.leagueSearchTimeout = setTimeout(async () => {
        if (this.leagueSearch.length < 2) {
          // If search is cleared or too short, load all leagues
          await this.loadAllLeagues()
          return
        }

        this.isSearchingLeagues = true
        try {
          // Make actual API call to search leagues
          const response = await axios.get(`/api/v2/leagues/search/${encodeURIComponent(this.leagueSearch)}`)
          console.log('League search results:', response.data)
          this.leagueResults = response.data
        } catch (error) {
          console.error('Error searching leagues:', error)
          this.leagueResults = []
        } finally {
          this.isSearchingLeagues = false
        }
      }, 300) // 300ms delay
    },
    
    async clearLeagueSearch() {
      this.leagueSearch = ''
      await this.loadAllLeagues()
    },
    
    async openLeagueDetails(league) {
      console.log('Opening league details:', league)
      this.selectedLeague = league
      this.currentLeagueIndex = this.leagueResults.findIndex(l => l.id === league.id)
      
      // Fetch detailed league data from API
      this.isLoadingLeague = true
      try {
        const response = await axios.get(`/api/v2/leagues/${league.id}`)
        console.log('Detailed league data:', response.data)
        
        // Update the selected league with detailed information
        this.selectedLeague = {
          ...league,
          ...response.data
        }
        
        // Search for projections stats for this league
        await this.searchProjectionsStats()
      } catch (error) {
        console.error('Error fetching league details:', error)
        // Keep the original league data if API call fails
      } finally {
        this.isLoadingLeague = false
      }
    },
    
    async previousLeague() {
      if (this.currentLeagueIndex > 0) {
        this.currentLeagueIndex--
        const league = this.leagueResults[this.currentLeagueIndex]
        this.selectedLeague = league
        
        // Fetch detailed league data from API
        this.isLoadingLeague = true
        try {
          const response = await axios.get(`/api/v2/leagues/${league.id}`)
          console.log('Detailed league data:', response.data)
          
          // Update the selected league with detailed information
          this.selectedLeague = {
            ...league,
            ...response.data
          }
          
          // Search for projections stats for this league
          await this.searchProjectionsStats()
        } catch (error) {
          console.error('Error fetching league details:', error)
          // Keep the original league data if API call fails
        } finally {
          this.isLoadingLeague = false
        }
      }
    },
    
    async nextLeague() {
      if (this.currentLeagueIndex < this.leagueResults.length - 1) {
        this.currentLeagueIndex++
        const league = this.leagueResults[this.currentLeagueIndex]
        this.selectedLeague = league
        
        // Fetch detailed league data from API
        this.isLoadingLeague = true
        try {
          const response = await axios.get(`/api/v2/leagues/${league.id}`)
          console.log('Detailed league data:', response.data)
          
          // Update the selected league with detailed information
          this.selectedLeague = {
            ...league,
            ...response.data
          }
          
          // Search for projections stats for this league
          await this.searchProjectionsStats()
        } catch (error) {
          console.error('Error fetching league details:', error)
          // Keep the original league data if API call fails
        } finally {
          this.isLoadingLeague = false
        }
      }
    },
    
    saveLeagueSettings() {
      console.log('Saving league settings:', this.selectedLeague)
      // Implement save logic here
      alert('League saved successfully!')
    },
    
    deleteLeague() {
      if (confirm('Are you sure you want to delete this league?')) {
        console.log('Deleting league:', this.selectedLeague)
        // Implement delete logic here
        this.selectedLeague = null
        this.leagueResults = this.leagueResults.filter(l => l.id !== this.selectedLeague?.id)
        alert('League deleted successfully!')
      }
    },
    
    // Team management methods for leagues
    async searchTeamsToAdd() {
      // Clear previous timeout
      if (this.addTeamSearchTimeout) {
        clearTimeout(this.addTeamSearchTimeout)
      }

      // Set new timeout for debounced search
      this.addTeamSearchTimeout = setTimeout(async () => {
        if (this.addTeamSearch.length < 2) {
          this.addTeamResults = []
          return
        }

        this.isSearchingAddTeam = true
        try {
          // Make API call to search teams (you can use the same teams search endpoint)
          const response = await axios.get(`/api/v2/teams/search/${encodeURIComponent(this.addTeamSearch)}`)
          console.log('Teams to add search results:', response.data)
          
          // Filter out teams that are already in the league
          const existingTeamIds = this.selectedLeague.teams?.map(t => t.team_id) || []
          this.addTeamResults = response.data.filter(team => !existingTeamIds.includes(team.id))
        } catch (error) {
          console.error('Error searching teams to add:', error)
          this.addTeamResults = []
        } finally {
          this.isSearchingAddTeam = false
        }
      }, 300) // 300ms delay
    },
    
    clearAddTeamSearch() {
      this.addTeamSearch = ''
      this.addTeamResults = []
    },
    
    // Handle search teams input from LeagueTeamsDisplay component
    handleSearchTeamsInput() {
      this.searchTeamsToAdd()
    },
    
    async addTeamToLeague(team) {
      try {
        // Make API call to add team to league
        const response = await axios.post(`/api/v2/leagues/${this.selectedLeague.id}/teams`, {
          team_id: team.id
        })
        console.log('Team added to league:', response.data)
        
        // Refresh the league data to get updated teams list
        await this.openLeagueDetails(this.selectedLeague)
        
        // Clear the search
        this.clearAddTeamSearch()
        this.showAddTeamSearch = false
        
        alert('Team added to league successfully!')
      } catch (error) {
        console.error('Error adding team to league:', error)
        alert('Failed to add team to league')
      }
    },
    
    async removeTeamFromLeague(teamItem) {
      if (confirm(`Are you sure you want to remove ${teamItem.team?.team_name} from this league?`)) {
        try {
          // Make API call to remove team from league
          const response = await axios.delete(`/api/v2/leagues/${this.selectedLeague.id}/teams/${teamItem.team_id}`)
          console.log('Team removed from league:', response.data)
          
          // Refresh the league data to get updated teams list
          await this.openLeagueDetails(this.selectedLeague)
          
          alert('Team removed from league successfully!')
        } catch (error) {
          console.error('Error removing team from league:', error)
          alert('Failed to remove team from league')
        }
      }
    },
    
    // Handle adding projection team to league
    async handleAddProjectionTeam({ projectionTeam, currentTeam, dataType, leagueAverage, dividePPGByLeagueAverage }) {
      try {
        console.log('Adding projection team to league:', projectionTeam, 'for current team:', currentTeam, 'with selected league:', this.selectedLeague)
        
        // You can implement custom logic here based on your requirements
        // For now, we'll just add the projection team as a regular team
        const response = await axios.post(`/api/v2/projections-settings/leagues/${this.selectedLeague.id}/teams`, {
          team_id: projectionTeam.team_id || projectionTeam.id,
          team_name: projectionTeam.team_name,
          league: projectionTeam.league,
          data_type: dataType,
          league_id: this.selectedLeague.id,
          ppg: projectionTeam.ppg,  
          leagueAverage: leagueAverage,
          dividePPGByLeagueAverage: projectionTeam.ppg / leagueAverage,
        })
        
        console.log('Projection team added to league:', response.data)
        
        // Refresh the league data to get updated teams list
        await this.openLeagueDetails(this.selectedLeague)
        
        alert(`Projection team "${projectionTeam.team_name}" added to league successfully!`)
      } catch (error) {
        console.error('Error adding projection team to league:', error)
        alert('Failed to add projection team to league')
      }
    }
  }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>
